'use client';

import { useCallback, useState } from 'react';
import { useDepositFlow } from '../../DepositSheet/hooks/useDepositFlow';
import type { DepositSuccessData, TransactionStatusResponse } from '../../DepositSheet/types';
import { useDirectDepositTransaction } from '../../hooks/useDirectDepositTransaction';
import { getDepositTrackingType, type CheckoutPaymentErrorContext } from '../utils/checkoutDialogTracking';

type CheckoutPaymentError = {
  message: string;
  context?: CheckoutPaymentErrorContext;
  depositType?: 'FTD' | 'SD';
};

type UseCheckoutPaymentOptions = {
  amount: string;
  defaultDepositType?: 'FTD' | 'SD';
  onSuccess: (depositData?: DepositSuccessData) => void;
  onError?: (error: CheckoutPaymentError) => void;
};

/**
 * Handles inline payment processing for methods that don't require a separate
 * details step (e.g. Pay by Bank, PaysafeCard, stored instruments). The
 * deposit transaction is created in the background while the checkout sheet
 * remains visible, showing a spinner in the Pay button.
 */
function useCheckoutPayment({ amount, defaultDepositType, onSuccess, onError }: UseCheckoutPaymentOptions) {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSuccess = useCallback(
    (transactionStatus: TransactionStatusResponse) => {
      setIsProcessing(false);
      onSuccess({ isFirstPayIn: transactionStatus.isFirstPayIn, daysSinceFtd: transactionStatus.daysSinceFtd });
    },
    [onSuccess]
  );

  const handleError = useCallback(
    (message: string, _errorStep?: string, transactionStatus?: TransactionStatusResponse, errorKey?: string) => {
      setIsProcessing(false);
      const depositType = transactionStatus != null ? getDepositTrackingType(transactionStatus.isFirstPayIn) : defaultDepositType;

      onError?.({
        message,
        context: {
          transactionState: transactionStatus?.state,
          errorKey: errorKey ?? transactionStatus?.errorKey,
        },
        ...(depositType ? { depositType } : {}),
      });
    },
    [defaultDepositType, onError]
  );

  const depositFlowInstance = useDepositFlow({
    namespace: 'global_sheets.deposit',
    onError: handleError,
    onSuccess: handleSuccess,
  });

  const handleBeforeTransaction = useCallback(() => {
    setIsProcessing(true);
  }, []);

  const { processDirectDeposit } = useDirectDepositTransaction({
    amount,
    depositFlowInstance,
    onError: handleError,
    onBeforeTransaction: handleBeforeTransaction,
  });

  const resetPayment = useCallback(() => {
    depositFlowInstance.resetFlow();
    setIsProcessing(false);
  }, [depositFlowInstance]);

  return {
    isProcessing,
    processPayment: processDirectDeposit,
    resetPayment,
  };
}

export { useCheckoutPayment };
export type { CheckoutPaymentError, UseCheckoutPaymentOptions };
